// E22-t2 (slice 4) — SCHEDULED REPORTS, as the browser may ask about them.
//
// Contract: vault/Apps/Forge/Pages/Reports.md — the org Reports screen reads
//   ONE list per KB (GET /api/:org/:kb/reports: the vault's declarations
//   joined with the org's consent and the last run, states armed | paused |
//   unarmed | invalid | orphaned), one history per report, and lands consent
//   changes on POST /api/:org/:kb/reports/:uuid/(arm|pause|disarm) — govern-
//   gated server-side, so this client just relays the guard's own sentence.
// Contract: vault/Flows/Scheduled Reports.md f2 — consent and content never
//   share a write: nothing here touches the report DOCUMENT.
//
// Shaped like src/api/kbs.ts, late-bound fetch included.
import { appFetch } from './transport';
import type { ReportSchedule } from '../../../platform/src/report-schedule';

export type ReportState = 'armed' | 'paused' | 'unarmed' | 'invalid' | 'orphaned';

/** One row of GET /api/:org/:kb/reports — the Durable Object's own join. */
export interface ReportRow {
  uuid: string;
  title: string;
  /** vault-relative path of the definition; null for an orphaned arm */
  path: string | null;
  state: ReportState;
  /** the envelope's or the orphan's refusal, in the server's words */
  invalidReason: string | null;
  schedule: ReportSchedule | null;
  nextDue: string | null;
  lastRun: ReportRunRow | null;
}

/** platform/src/reports.ts ReportRun, as the wire spells it. */
export interface ReportRunRow {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  outcome: 'running' | 'completed' | 'completed_with_warnings' | 'failed';
  commitOid: string | null;
  snapshotKey: string | null;
  reason: string;
  tokensSpent: number;
}

export interface ReportsClient {
  listReports(org: string, kb: string): Promise<{ commit: string | null; reports: ReportRow[] }>;
  runsFor(org: string, kb: string, reportUuid: string): Promise<ReportRunRow[]>;
  /** the three consent verbs — govern-gated by the Worker, refusals relayed */
  setConsent(
    org: string,
    kb: string,
    reportUuid: string,
    verb: 'arm' | 'pause' | 'disarm',
  ): Promise<{ nextAlarm: string | null }>;
}

const seg = encodeURIComponent;
export const reportsEndpoint = (org: string, kb: string): string =>
  `/api/${seg(org)}/${seg(kb)}/reports`;

async function refusalOf(response: Response, what: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as {
    guard?: string;
    reason?: string;
    message?: string;
    error?: string;
  };
  const said = body.reason ?? body.message ?? body.error;
  return new Error(
    `${body.guard ? `${body.guard}: ` : ''}${said ?? `${what} answered ${response.status}`}`,
  );
}

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

function asRun(raw: Record<string, unknown> | null): ReportRunRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  return {
    id: String(raw.id ?? ''),
    startedAt: String(raw.startedAt ?? ''),
    finishedAt: str(raw.finishedAt),
    outcome: (raw.outcome ?? 'failed') as ReportRunRow['outcome'],
    commitOid: str(raw.commitOid),
    snapshotKey: str(raw.snapshotKey),
    reason: String(raw.reason ?? ''),
    tokensSpent: Number(raw.tokensSpent ?? 0),
  };
}

export function createReportsClient(
  fetchImpl: typeof fetch = (input, init) => appFetch(input, init),
): ReportsClient {
  return {
    async listReports(org, kb) {
      const path = reportsEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${path}`);
      const body = (await response.json()) as {
        commit?: string | null;
        reports?: Array<Record<string, unknown>>;
      };
      const reports: ReportRow[] = [];
      for (const entry of body.reports ?? []) {
        const uuid = str(entry.uuid);
        if (uuid === null) continue;
        reports.push({
          uuid,
          title: str(entry.title) ?? uuid,
          path: str(entry.path),
          state: (entry.state ?? 'invalid') as ReportState,
          invalidReason: str(entry.invalidReason),
          schedule: (entry.schedule ?? null) as ReportSchedule | null,
          nextDue: str(entry.nextDue),
          lastRun: asRun((entry.lastRun ?? null) as Record<string, unknown> | null),
        });
      }
      return { commit: str(body.commit), reports };
    },

    async runsFor(org, kb, reportUuid) {
      const path = `${reportsEndpoint(org, kb)}/${seg(reportUuid)}/runs`;
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${path}`);
      const body = (await response.json()) as { runs?: Array<Record<string, unknown>> };
      return (body.runs ?? []).map((raw) => asRun(raw)).filter((run): run is ReportRunRow => run !== null);
    },

    async setConsent(org, kb, reportUuid, verb) {
      const path = `${reportsEndpoint(org, kb)}/${seg(reportUuid)}/${verb}`;
      const response = await fetchImpl(path, { method: 'POST', credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `POST ${path}`);
      const body = (await response.json()) as { nextAlarm?: string | null };
      return { nextAlarm: str(body.nextAlarm) };
    },
  };
}
